import React, { useEffect, useState } from 'react';
import { FaEye, FaFileAlt } from 'react-icons/fa';
import '../../styles/user/totalCandidatesPage.css';
import {
  fetchTotalCandidates
} from '../../services/user/api';
import { Search } from 'lucide-react';
import { useNavigate } from "react-router-dom";

const TotalCandidatesPage = ({ onViewFinalReport, onViewReportTwo }) => {

  const [candidates, setCandidates] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("createddate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isAsc, setIsAsc] = useState(true);
  const navigate = useNavigate();
  const startIndex = (currentPage - 1) * entriesPerPage;
  const totalPages = Math.ceil(totalElements / entriesPerPage);


  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(handler);
  }, [search]);


  useEffect(() => {
    loadCandidates(
      currentPage - 1,
      entriesPerPage,
      debouncedSearch,
      sortField,
      sortOrder
    );
  }, [currentPage, entriesPerPage, debouncedSearch, sortField, sortOrder]);

  const loadCandidates = async (page, size, searchTerm, sortField, sortOrder) => {
    try {
      setLoading(true);
      page = Math.max(0, page);

      const res = await fetchTotalCandidates(
        page,
        size,
        searchTerm,
        sortField,
        sortOrder
      );

      const pageData = res.data.data;

      setCandidates(pageData.content || []);
      setTotalElements(pageData.totalElements || 0);
      setFetchError(null);

    } catch (error) {
      console.error("Fetch error:", error);
      setFetchError("An error occurred while fetching data.");
      setCandidates([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };


  // handle the sort with consistency icons and defualt sort asc . 
  const handleSort = (column) => {
    if (sortField === column) {
      // Same column -> toggle
      const newIsAsc = !isAsc;
      setIsAsc(newIsAsc);
      setSortOrder(newIsAsc ? "asc" : "desc");
    } else {
      // Different column -> default to ascending
      setSortField(column);
      setIsAsc(true);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };


  const renderSortArrow = (field) => {
    const isActive = sortField === field;

    return (
      <span
        className={`sort-icons ${isActive ? "sort-active" : ""
          }`}
      >
        {/* showing the next action button  */}
        {!isActive
          ? "▲▼"
          : sortOrder === "asc"
            ? "▼" : "▲"
        }
      </span>
    );
  };


  return (
    <>
      <p className="client-header">Total Candidates</p>

      <div className="client-wrapper">


        <div className="clinet-table-controls">

          <div className="entries-select">
            Show{" "}
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>{" "}
            entries
          </div>

          <div className="client-search-box">
            <div className="client-search-input-wrapper">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by any field"
              />
              <Search className="client-search-icon" size={18} />
            </div>
          </div>
        </div>

        <div className="clinet-table-container">
          <table className="candidates-clinet-table">
            <colgroup>
              <col style={{ width: "130px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "220px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "195px" }} />
              <col style={{ width: "150px" }} />
            </colgroup>
            <thead>
              <tr>
                <th onClick={() => handleSort("candidateid")}>Candidate ID {renderSortArrow("candidateid")}</th>
                <th onClick={() => handleSort("fullname")}>Name {renderSortArrow("fullname")}</th>
                <th onClick={() => handleSort("email")}>Email {renderSortArrow("email")}</th>
                <th onClick={() => handleSort("mobileNumber")}>Mobile {renderSortArrow("mobileNumber")}</th>
                <th onClick={() => handleSort("dateOfBirth")}>DOB {renderSortArrow("dateOfBirth")}</th>
                <th onClick={() => handleSort("caseInitiatedDateOnlyAsString")}>Created Date {renderSortArrow("caseInitiatedDateOnlyAsString")}</th>
                <th onClick={() => handleSort("bgcStartDateOnlyString")}>BGC Start Date {renderSortArrow("bgcStartDateOnlyString")}</th>
                <th onClick={() => handleSort("reportDateOnlyString")}>Completed Date {renderSortArrow("reportDateOnlyString")}</th>
                <th onClick={() => handleSort("bgcStatus")}>Status {renderSortArrow("bgcStatus")}</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="loading">
                    Loading Candidates...
                  </td>
                </tr>
              ) : fetchError ? (
                <tr>
                  <td colSpan="10" className="no-data">
                    {fetchError}
                  </td>
                </tr>
              ) : candidates.length > 0 ? (
                candidates.map(candidate => (
                  <tr key={candidate.candidateid}>
                    <td>{candidate.candidateid}</td>
                    <td>{candidate.fullname}</td>
                    <td>{candidate.email}</td>
                    <td>{candidate.mobileNumber}</td>
                    <td>{candidate.dateOfBirth}</td>
                    <td>{candidate.caseInitiatedDateOnlyAsString || "---"}</td>
                    <td>{candidate.bgcStartDateOnlyString || "---"}</td>
                    <td>{candidate.reportDateOnlyString || "---"}</td>
                    <td>
                      <span className={`status-tag ${String(candidate.bgcStatus).toLowerCase()}`}>
                        {candidate.bgcStatus}
                      </span>
                    </td>
                    <td>
                      <button
                        className="icon-btn"
                        onClick={() => onViewReportTwo(candidate.candidateid)}
                        disabled={candidate.bgcStatus === 'PENDING_SUBMISSION'}
                        title="View Report"

                      >
                        <FaEye />
                      </button>

                      <button
                        className="icon-btn"
                        onClick={() => navigate(`/client/finalreport/${candidate.candidateid}`)}
                        disabled={candidate.bgcStatus !== 'COMPLETED'}
                        title="Download PDF"

                      >
                        <FaFileAlt />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="no-data">
                    No matching candidates found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalElements > 0 && (
          <div className="pagination">
            <div className="pagination-summary">
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + candidates.length, totalElements)} of{" "}
              {totalElements} entries
            </div>

            <div className="pagination-buttons-user">
              <button
                className="page-btn"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                ←
              </button>

              <button className="page-btn active">{currentPage}</button>

              <button
                className="page-btn"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TotalCandidatesPage;